import { AlertTriangle, Info } from "lucide-react";
import { getPlatformStats } from "@/api/client";
import type { LaneStatusRow, StoreStatusRow } from "@/api/types";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { HorizontalBarChart } from "@/components/HorizontalBarChart";
import { StatCard } from "@/components/StatCard";
import { ChartSkeleton, ErrorState, Skeleton, TableSkeleton } from "@/components/states";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { formatCompact, formatCurrency, formatDateTime, formatNumber } from "@/lib/format";

const STALE_PING_MS = 24 * 3_600_000;

/**
 * A reference figure that rides in the title row: an eyebrow label with a
 * hover definition, the exact value beneath. Deliberately small — these are
 * facts people ask for, not what the page is about, so they must not compete
 * with the tiles.
 */
function HeadlineFact({
  label,
  value,
  hint,
}: {
  label: string;
  /** Undefined or null renders a shimmer placeholder while data loads. */
  value?: string | null;
  hint: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 whitespace-nowrap sm:px-5 sm:first:pl-0 sm:last:pr-0">
      <dt className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground">
        {label}
        <span title={hint} className="cursor-help">
          <Info className="h-3.5 w-3.5 opacity-50" aria-label={hint} role="img" />
        </span>
      </dt>
      <dd className="text-lg font-semibold leading-tight text-accent-foreground">
        {value ?? <Skeleton className="h-5 w-16" />}
      </dd>
    </div>
  );
}

function isStale(iso: string | null): boolean {
  return !iso || Date.now() - new Date(iso).getTime() > STALE_PING_MS;
}

/** Ping recency with an icon + label when stale — never color alone. */
function PingCell({ iso }: { iso: string | null }) {
  if (!isStale(iso)) {
    return <span className="whitespace-nowrap">{formatDateTime(iso)}</span>;
  }
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap"
      style={{ color: "var(--viz-status-critical)" }}
    >
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
      {formatDateTime(iso)} (stale)
    </span>
  );
}

function DateTimeCell({ iso }: { iso: string | null }) {
  return <span className="whitespace-nowrap">{formatDateTime(iso)}</span>;
}

// Never-pinged/never-checked-out sorts as oldest, not missing — "never" is
// the most stale a timestamp can be.
function epoch(iso: string | null): number {
  return iso ? new Date(iso).getTime() : 0;
}

const storeColumns: Column<StoreStatusRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  {
    header: "POS Vendor",
    cell: (r) => r.posVendor ?? "—",
    sortValue: (r) => r.posVendor,
  },
  {
    header: "Waterfall fees / transaction",
    headerTooltip:
      "This includes store and vendor waterfall fees. The store fee is assumed to be the standard $0.10 for a campaign.",
    align: "right",
    cell: (r) => formatCurrency(r.waterfallFeesPerTransaction),
    sortValue: (r) => r.waterfallFeesPerTransaction,
  },
  {
    header: "GFT Profit / transaction",
    headerTooltip:
      "This includes store and vendor waterfall fees. The store fee is assumed to be the standard $0.10 for a campaign.",
    align: "right",
    cell: (r) => formatCurrency(r.profitPerTransaction),
    sortValue: (r) => r.profitPerTransaction,
  },
  {
    header: "Last ping",
    cell: (r) => <PingCell iso={r.lastPingedAt} />,
    sortValue: (r) => epoch(r.lastPingedAt),
    // Sort by recency, export the raw ISO timestamp.
    csvValue: (r) => r.lastPingedAt,
  },
  {
    header: "Last checkout",
    cell: (r) => <DateTimeCell iso={r.lastCheckoutAt} />,
    sortValue: (r) => epoch(r.lastCheckoutAt),
    csvValue: (r) => r.lastCheckoutAt,
  },
  {
    header: "Lanes",
    align: "right",
    cell: (r) => formatNumber(r.laneCount),
    sortValue: (r) => r.laneCount,
  },
  {
    header: "Active campaigns",
    align: "right",
    cell: (r) => formatNumber(r.activeCampaignCount),
    sortValue: (r) => r.activeCampaignCount,
  },
  {
    header: "Transactions",
    align: "right",
    cell: (r) => formatNumber(r.totalTransactions),
    sortValue: (r) => r.totalTransactions,
  },
];

const laneColumns: Column<LaneStatusRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  { header: "Lane", align: "right", cell: (r) => r.lane, sortValue: (r) => r.lane },
  {
    header: "Plugin",
    cell: (r) => r.pluginId ?? "—",
    sortValue: (r) => r.pluginId,
  },
  {
    header: "Last ping",
    cell: (r) => <PingCell iso={r.lastPingedAt} />,
    sortValue: (r) => epoch(r.lastPingedAt),
    csvValue: (r) => r.lastPingedAt,
  },
  {
    header: "Last checkout",
    cell: (r) => <DateTimeCell iso={r.lastCheckoutAt} />,
    sortValue: (r) => epoch(r.lastCheckoutAt),
    csvValue: (r) => r.lastCheckoutAt,
  },
  {
    header: "Transactions",
    align: "right",
    cell: (r) => formatNumber(r.totalTransactions),
    sortValue: (r) => r.totalTransactions,
  },
];

export function PlatformStatsPage() {
  const state = useApiData(getPlatformStats);
  // The page shell (heading, tile labels, card titles) renders immediately;
  // each section shows a shimmer placeholder until the data lands.
  const data = state.status === "ready" ? state.data : null;

  const staleStores =
    data?.storeStatus.filter((s) => isStale(s.lastPingedAt)).length ?? 0;

  // Aggregate the per-store DMA rows into the two ranked charts.
  const byDma = new Map<string, { stores: number; units: number }>();
  for (const row of data?.storesByDma ?? []) {
    const agg = byDma.get(row.dma) ?? { stores: 0, units: 0 };
    agg.stores += 1;
    agg.units += row.units;
    byDma.set(row.dma, agg);
  }
  const storeCountByDma = [...byDma.entries()]
    .map(([name, agg]) => ({ name, value: agg.stores }))
    .sort((a, b) => b.value - a.value);
  const unitsByDma = [...byDma.entries()]
    .map(([name, agg]) => ({ name, value: agg.units }))
    .sort((a, b) => b.value - a.value);

  // Attention first: stale stores float to the top of the status table.
  const sortedStores = [...(data?.storeStatus ?? [])].sort(
    (a, b) => Number(isStale(b.lastPingedAt)) - Number(isStale(a.lastPingedAt)),
  );

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
        <div>
          <h1 className="text-xl font-semibold">Platform statistics</h1>
          <p className="text-sm text-muted-foreground">
            Store and lane health across the network, last 30 days.
          </p>
        </div>
        {/* The "all time" scope is visible text, not a tooltip, because these
            are the only all-time figures on a 30-day page; it is the strip's
            group label so it is said once. The Sunlit Lilac strip (the accent
            shade) sets the facts apart from the tiles without making them a
            card of their own. */}
        <div className="flex flex-col gap-1.5 rounded-lg bg-accent px-5 py-2.5 sm:flex-row sm:items-center sm:gap-0 sm:divide-x sm:divide-accent-foreground/20">
          <span className="text-xs font-semibold uppercase tracking-wider text-accent-foreground sm:pr-5">
            All time
          </span>
          <dl className="flex flex-wrap gap-x-6 gap-y-2 sm:gap-0 sm:divide-x sm:divide-accent-foreground/20 sm:pl-5">
            <HeadlineFact
              label="Registered shoppers"
              value={data && formatNumber(data.tiles.shoppersWithPhoneAllTime)}
              hint="Shoppers with a home store and a phone number"
            />
            <HeadlineFact
              label="With a wallet"
              value={data && formatNumber(data.tiles.shoppersWithWalletAllTime)}
              hint="Shoppers with a home store and a blockchain wallet"
            />
            <HeadlineFact
              label="Marketing opt-ins"
              value={
                data && formatNumber(data.tiles.shoppersOptedInForMarketingAllTime)
              }
              hint="Shoppers with a home store who are opted in to marketing"
            />
          </dl>
        </div>
      </div>

      {state.status === "error" ? (
        <ErrorState message={state.message} onRetry={state.reload} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Stores"
              value={data && formatCompact(data.tiles.storeCount)}
            />
            <StatCard
              label="Stores pinging (1 day)"
              value={data && formatCompact(data.tiles.storesPingedLastDay)}
              delta={
                staleStores > 0
                  ? {
                      text: `${staleStores} stale in table below`,
                      direction: "up",
                      upIsGood: false,
                    }
                  : undefined
              }
            />
            <StatCard
              label="Stores with checkouts (1 day)"
              value={data && formatCompact(data.tiles.storesCheckedOutLastDay)}
            />
            <StatCard
              label="Lanes"
              value={data && formatCompact(data.tiles.laneCount)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Transactions"
              value={data && formatCompact(data.tiles.transactionCount30d)}
            />
            <StatCard
              label="Identified transactions"
              value={data && formatCompact(data.tiles.identifiedTransactionCount30d)}
            />
            <StatCard
              label="Unique identified users"
              value={data && formatCompact(data.tiles.uniqueIdentifiedUsers30d)}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Active stores by DMA</CardTitle>
                <CardDescription>
                  Stores with checkout activity
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data ? (
                  <HorizontalBarChart data={storeCountByDma} seriesName="Stores" />
                ) : (
                  <ChartSkeleton />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Units by DMA</CardTitle>
              </CardHeader>
              <CardContent>
                {data ? (
                  <HorizontalBarChart data={unitsByDma} seriesName="Units" />
                ) : (
                  <ChartSkeleton />
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Store status</CardTitle>
                <CardDescription>
                  Stores that haven't pinged in 24 hours sort to the top.
                  {data
                    ? ` Profit/transaction is ${formatCurrency(data.revenuePerTransaction)} revenue minus waterfall fees.`
                    : null}
                </CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="store-status"
                  columns={storeColumns}
                  rows={sortedStores}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={storeColumns}
                  rows={sortedStores}
                  rowKey={(r, i) => `${r.storeName}-${i}`}
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Lane status</CardTitle>
                <CardDescription>
                  Per-lane plugin version and activity
                </CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="lane-status"
                  columns={laneColumns}
                  rows={data.laneStatus}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={laneColumns}
                  rows={data.laneStatus}
                  rowKey={(r, i) => `${r.storeName}-${r.lane}-${i}`}
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
